import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useParams } from "react-router-dom";
import api from "../api";
import { ButtonStatus } from "../common/constants";
import { ReactComponent as TenBackwardIcon } from "../assets/images/icons/10_backward.svg";
import { ReactComponent as TenForwardIcon } from "../assets/images/icons/10_forward.svg";
import { ReactComponent as OneXIcon } from "../assets/images/icons/1x.svg";
import { ReactComponent as OneHalfXIcon } from "../assets/images/icons/1.5x.svg";
import { ReactComponent as TwoXIcon } from "../assets/images/icons/2x.svg";
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg";
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg";
import { ReactComponent as CloseIcon } from "../assets/images/icons/ic-close.svg";
import { ReactComponent as SkipTabIcon } from "../assets/images/icons/ic-skip-tab.svg";
import { ReactComponent as ExitIcon } from "../assets/images/icons/ic-exit.svg";
import { ReactComponent as PlayIcon } from "../assets/images/icons/ic-play.svg";
import { ReactComponent as PauseIcon } from "../assets/images/icons/ic-pause.svg";
import Button from "../components/Button";
import AlertDialog from "../components/dialogs/AlertDialog";
import Dialog from "../components/dialogs/Dialog";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import { DialogStatus } from "../enums";
import { formatSecondsDuration } from "../utils";
import styles from "./RecordingsEditor.module.css";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const MIN_SEGMENT_DURATION = 0.2;
const MIN_TIMELINE_WIDTH = 80;
const FORCE_TEST_DATA = false;
const TEST_VIDEO_URL = "https://starting-finance-dev.fra1.digitaloceanspaces.com/development/lessons/Velv%20SAL/Edizione%202025/Test%20Video%20Worker/proxy.mp4";
const TEST_SEGMENTS = [
  {
    end: 393.79,
    start: 58.0,
  },
];

const normalizeSegments = (items = [], duration = 0) => {
  return items
    .filter((segment) => segment && Number.isFinite(Number(segment.start)) && Number.isFinite(Number(segment.end)))
    .map((segment, index) => {
      const start = Number(segment.start);
      const end = Number(segment.end);
      return {
        id: `${Date.now()}-${index}`,
        start: clamp(Math.min(start, end), 0, duration || Math.max(start, end)),
        end: clamp(Math.max(start, end), 0, duration || Math.max(start, end)),
      };
    })
    .filter((segment) => segment.end - segment.start >= MIN_SEGMENT_DURATION)
    .sort((a, b) => a.start - b.start);
};

const getSegmentNeighbors = (items = [], segmentId) => {
  const sorted = [...items].sort((a, b) => a.start - b.start);
  const index = sorted.findIndex((segment) => segment.id === segmentId);
  if (index < 0) {
    return { prevSegment: null, nextSegment: null };
  }
  return {
    prevSegment: index > 0 ? sorted[index - 1] : null,
    nextSegment: index < sorted.length - 1 ? sorted[index + 1] : null,
  };
};

const formatTimelineTickLabel = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  if (safe >= 60 * 60) {
    const hours = Math.floor(safe / 3600);
    const mins = Math.floor((safe % 3600) / 60);
    return `${hours}h ${String(mins).padStart(2, "0")}m`;
  }
  return formatSecondsDuration(safe);
};

const stripQueryAndHash = (url = "") => url.split("?")[0].split("#")[0].toLowerCase();
const isMp4Url = (url = "") => stripQueryAndHash(url).endsWith(".mp4");
const isM3u8Url = (url = "") => stripQueryAndHash(url).endsWith(".m3u8");

const formatSecondsToHms = (seconds = 0) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const parseHmsToSeconds = (value = "") => {
  const normalized = String(value).trim();
  if (!normalized) return NaN;

  const parts = normalized.split(":").map((item) => Number(item));
  if (parts.some((item) => !Number.isFinite(item) || item < 0)) return NaN;

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 1) {
    return parts[0];
  }

  return NaN;
};

const getClientXFromEvent = (event) => {
  if (typeof event?.clientX === "number") {
    return event.clientX;
  }

  const touch = event?.touches?.[0] || event?.changedTouches?.[0];
  return typeof touch?.clientX === "number" ? touch.clientX : null;
};

const RecordingsEditor = () => {
  const { lesson_id } = useParams();

  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineViewportRef = useRef(null);
  const waveformRef = useRef(null);
  const waveformCanvasRef = useRef(null);
  const audioRawRef = useRef(null);
  const suppressNextSeekRef = useRef(false);
  const addSegmentWarningTimeoutRef = useRef(null);
  const centerOnCursorAfterZoomRef = useRef(false);

  const [videoUrl, setVideoUrl] = useState(() => (FORCE_TEST_DATA ? TEST_VIDEO_URL : ""));
  const [videoTitle, setVideoTitle] = useState(() => (FORCE_TEST_DATA ? "Test recording" : ""));
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [displayCurrentTime, setDisplayCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  const [segments, setSegments] = useState(() => (FORCE_TEST_DATA ? normalizeSegments(TEST_SEGMENTS) : []));
  const [zoom, setZoom] = useState(1);

  const [dragState, setDragState] = useState(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);
  const [, setSegmentsUndoStack] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);
  const [addSegmentWarning, setAddSegmentWarning] = useState("");
  const [skipSegmentsPlayback, setSkipSegmentsPlayback] = useState(false);
  const [waveformUrl, setWaveformUrl] = useState("");
  const [loadingWaveform, setLoadingWaveform] = useState(false);
  const [wavePeaks, setWavePeaks] = useState([]);
  const [rawWavePeaks, setRawWavePeaks] = useState([]);
  const [audioReady, setAudioReady] = useState(false);
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(0);
  const [segmentDialog, setSegmentDialog] = useState({ open: false, segmentId: null, start: "", end: "" });
  const [deleteSegmentDialog, setDeleteSegmentDialog] = useState({ open: false, segmentId: null });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const timelineWidth = useMemo(() => {
    if (!timelineViewportWidth) return MIN_TIMELINE_WIDTH;
    return Math.max(timelineViewportWidth * zoom, MIN_TIMELINE_WIDTH, timelineViewportWidth);
  }, [zoom, timelineViewportWidth]);

  const zoomMax = useMemo(() => {
    if (!duration || !timelineViewportWidth) return 10;

    // Hard safety limit for canvas width (avoid browser limits ~16k–32k px)
    const MAX_CANVAS_WIDTH = 16000;
    const dpr = window.devicePixelRatio || 1;

    // timelineWidth = timelineViewportWidth * zoom
    // canvas.width = timelineWidth * dpr
    // => timelineViewportWidth * zoom * dpr <= MAX_CANVAS_WIDTH
    const maxZoomByCanvas =
      MAX_CANVAS_WIDTH / (timelineViewportWidth * dpr);

    const targetMaxPxPerSecond =
      duration >= 3 * 60 * 60 ? 6 :
        duration >= 2 * 60 * 60 ? 7 :
          duration >= 60 * 60 ? 8 :
            duration >= 30 * 60 ? 10 :
              12;

    const minByDuration =
      duration >= 3 * 60 * 60 ? 20 :
        duration >= 2 * 60 * 60 ? 16 :
          duration >= 60 * 60 ? 12 :
            10;

    const computed = (duration * targetMaxPxPerSecond) / timelineViewportWidth;

    const logicalMax = clamp(computed, minByDuration, 120);

    // Final zoom max is the minimum between logical zoom and canvas safety zoom
    return Number(clamp(Math.min(logicalMax, maxZoomByCanvas), 1, 120).toFixed(1));
  }, [duration, timelineViewportWidth]);

  const effectivePxPerSecond = useMemo(() => {
    if (!duration) return 1;
    return timelineWidth / duration;
  }, [timelineWidth, duration]);

  const playbackTickMs = useMemo(() => {
    if (playbackRate >= 2) return 500;
    if (playbackRate >= 1.5) return 750;
    return 1000;
  }, [playbackRate]);

  useEffect(() => {
    setZoom((prev) => clamp(prev, 1, zoomMax));
  }, [zoomMax]);

  useEffect(() => {
    if (!isPlaying) {
      setDisplayCurrentTime(currentTime);
    }
  }, [currentTime, isPlaying]);

  useEffect(() => {
    if (!isPlaying) return;

    setDisplayCurrentTime(videoRef.current?.currentTime ?? currentTime);

    const timer = setInterval(() => {
      setDisplayCurrentTime(videoRef.current?.currentTime ?? 0);
    }, playbackTickMs);

    return () => clearInterval(timer);
  }, [isPlaying, playbackTickMs, currentTime]);

  useEffect(() => {
    const viewport = timelineViewportRef.current;
    if (!viewport) return;

    const updateViewportWidth = () => {
      setTimelineViewportWidth(viewport.clientWidth || 0);
    };

    updateViewportWidth();

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateViewportWidth);
      observer.observe(viewport);
    }

    window.addEventListener("resize", updateViewportWidth);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", updateViewportWidth);
    };
  }, []);

  useEffect(() => {
    if (!centerOnCursorAfterZoomRef.current) return;
    const viewport = timelineViewportRef.current;
    if (!viewport || !duration) {
      centerOnCursorAfterZoomRef.current = false;
      return;
    }

    const viewportWidth = viewport.clientWidth;
    if (timelineWidth <= viewportWidth) {
      viewport.scrollLeft = 0;
      centerOnCursorAfterZoomRef.current = false;
      return;
    }

    const cursorX = (currentTime / duration) * timelineWidth;
    const maxScrollLeft = Math.max(0, timelineWidth - viewportWidth);
    const nextScrollLeft = clamp(cursorX - viewportWidth / 2, 0, maxScrollLeft);
    viewport.scrollLeft = nextScrollLeft;

    centerOnCursorAfterZoomRef.current = false;
  }, [timelineWidth, currentTime, duration]);

  useEffect(() => {
    if (FORCE_TEST_DATA) {
      return;
    }

    const getLesson = async () => {
      try {
        const lesson = await api.get(`/admin/lessons/${lesson_id}`);
        setVideoTitle(lesson?.name || "");
        setVideoUrl(lesson?.video_proxy_url || "");
        setWaveformUrl(lesson?.video_waveform_url || "");

        const initial = normalizeSegments(
          lesson?.video_segments_final || lesson?.video_segments_auto || [],
          0
        );
        setSegments(initial);
      } catch (e) {
        console.error(e);
      }
    };

    if (lesson_id) {
      getLesson();
    }
  }, [lesson_id]);

  useEffect(() => {
    return () => {
      if (addSegmentWarningTimeoutRef.current) {
        clearTimeout(addSegmentWarningTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!videoUrl) {
      setWavePeaks([]);
      audioRawRef.current = null;
      setAudioReady(false);
      return;
    }

    let cancelled = false;

    const loadWaveformJson = async () => {
      try {
        setLoadingWaveform(true);
        const response = await fetch(waveformUrl);
        const payload = await response.json();
        const data = Array.isArray(payload?.data) ? payload.data : [];

        if (!cancelled) {
          const normalized = data.map((value) =>
            Math.max(0, Math.min(1, Number(value) || 0))
          );
          setRawWavePeaks(normalized);
          setWavePeaks([]);
          audioRawRef.current = null;
          setAudioReady(false);
        }
      } catch (e) {
        if (!cancelled) {
          setRawWavePeaks([]);
          setWavePeaks([]);
          audioRawRef.current = null;
          setAudioReady(false);
        }
        console.error("Waveform JSON load error", e);
      } finally {
        if (!cancelled) {
          setLoadingWaveform(false);
        }
      }
    };

    const decodeAudio = async () => {
      try {
        setLoadingWaveform(true);
        const response = await fetch(videoUrl);
        const audioBufferData = await response.arrayBuffer();
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(audioBufferData);
        const raw = audioBuffer.getChannelData(0);

        if (!cancelled) {
          audioRawRef.current = Float32Array.from(raw);
          setAudioReady(true);
          setRawWavePeaks([]);
          setWavePeaks([]);
        }

        audioContext.close();
      } catch (e) {
        if (!cancelled) {
          audioRawRef.current = null;
          setAudioReady(false);
          setWavePeaks([]);
        }
        console.error("Waveform generation error", e);
      } finally {
        if (!cancelled) {
          setLoadingWaveform(false);
        }
      }
    };

    if (isM3u8Url(videoUrl) && waveformUrl) {
      loadWaveformJson();
    } else if (isMp4Url(videoUrl)) {
      decodeAudio();
    } else {
      setRawWavePeaks([]);
      setWavePeaks([]);
      audioRawRef.current = null;
      setAudioReady(false);
      setLoadingWaveform(false);
    }

    return () => {
      cancelled = true;
    };
  }, [videoUrl, waveformUrl]);

  useEffect(() => {
    if (!audioReady || !audioRawRef.current) return;

    const raw = audioRawRef.current;
    const targetSamples = Math.max(600, Math.floor(timelineWidth));
    const blockSize = Math.max(1, Math.floor(raw.length / targetSamples));
    const peaks = [];

    for (let i = 0; i < targetSamples; i++) {
      const start = i * blockSize;
      const end = Math.min(raw.length, start + blockSize);
      let max = 0;
      for (let j = start; j < end; j++) {
        const value = Math.abs(raw[j]);
        if (value > max) max = value;
      }
      peaks.push(max);
    }

    setWavePeaks(peaks);
  }, [audioReady, timelineWidth]);

  useEffect(() => {
    const onKeyDown = async (event) => {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isTypingContext =
        target?.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";

      if (event.metaKey && !isTypingContext && (event.key === "Backspace" || event.key === "Delete")) {
        if (selectedSegmentIds.length > 0) {
          event.preventDefault();
          const snapshot = segments.map((segment) => ({ ...segment }));
          setSegmentsUndoStack((prev) => [...prev, snapshot]);
          setSegments((prev) => prev.filter((segment) => !selectedSegmentIds.includes(segment.id)));
          setSelectedSegmentIds([]);
          setActiveSegmentId(null);
          setDragState(null);
        }
        return;
      }

      if (event.metaKey && !isTypingContext && event.key.toLowerCase() === "z") {
        event.preventDefault();
        setSegmentsUndoStack((prev) => {
          if (prev.length === 0) return prev;
          const next = [...prev];
          const restore = next.pop();
          if (restore) {
            setSegments(restore.map((segment) => ({ ...segment })));
            setSelectedSegmentIds([]);
            setActiveSegmentId(null);
          }
          return next;
        });
        return;
      }

      if (event.code !== "Space") return;
      if (isTypingContext) return;

      const video = videoRef.current;
      if (!video) return;

      event.preventDefault();
      event.stopPropagation();
      try {
        if (video.paused) {
          await video.play();
        } else {
          video.pause();
        }
      } catch (e) {
        console.error("Toggle play/pause error", e);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [segments, selectedSegmentIds]);

  const toggleSegmentSelection = useCallback((segmentId) => {
    setSelectedSegmentIds((prev) =>
      prev.includes(segmentId)
        ? prev.filter((id) => id !== segmentId)
        : [...prev, segmentId]
    );
  }, []);

  const handleOutsideSegmentClick = useCallback((event) => {
    const target = event.target;
    const isInsideSegment =
      target?.closest?.(`.${styles.segment}`) ||
      target?.closest?.(`.${styles.handle}`) ||
      target?.closest?.(`.${styles.segmentRow}`) ||
      target?.closest?.(`.${styles.segmentRowMain}`);

    if (!isInsideSegment) {
      setSelectedSegmentIds([]);
    }
  }, []);

  useEffect(() => {
    const canvas = waveformCanvasRef.current;
    const waveformElement = waveformRef.current;
    if (!canvas || !waveformElement || !wavePeaks.length) return;

    const height = Math.max(1, waveformElement.clientHeight);
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.floor(timelineWidth * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${timelineWidth}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const rootStyles = getComputedStyle(document.documentElement);
    const textRgb = rootStyles.getPropertyValue("--text-color-rgb")?.trim() || "36, 36, 36";
    const primaryColor = rootStyles.getPropertyValue("--primary")?.trim() || "#5C7CFA";

    ctx.clearRect(0, 0, timelineWidth, height);
    ctx.fillStyle = `rgba(${textRgb}, 0.04)`;
    ctx.fillRect(0, 0, timelineWidth, height);

    const centerY = height / 2;
    const barWidth = Math.max(1, timelineWidth / wavePeaks.length);

    // --- Smooth continuous waveform ---
    const LOG_FACTOR = 9;
    const VISUAL_GAIN = 1.4;
    const MIN_VISUAL_AMPLITUDE = 0.01; // ensures visible line even for silent audio

    ctx.beginPath();
    ctx.moveTo(0, centerY);

    for (let i = 0; i < wavePeaks.length; i++) {
      const rawAmplitude = wavePeaks[i] || 0;

      const compressed =
        rawAmplitude > 0
          ? Math.log10(1 + LOG_FACTOR * rawAmplitude) / Math.log10(1 + LOG_FACTOR)
          : 0;

      const amplitude = Math.max(
        MIN_VISUAL_AMPLITUDE,
        Math.min(1, compressed * VISUAL_GAIN)
      );

      const y = centerY - amplitude * (height * 0.45);
      const x = i * barWidth;

      ctx.lineTo(x, y);
    }

    for (let i = wavePeaks.length - 1; i >= 0; i--) {
      const rawAmplitude = wavePeaks[i] || 0;

      const compressed =
        rawAmplitude > 0
          ? Math.log10(1 + LOG_FACTOR * rawAmplitude) / Math.log10(1 + LOG_FACTOR)
          : 0;

      const amplitude = Math.max(
        MIN_VISUAL_AMPLITUDE,
        Math.min(1, compressed * VISUAL_GAIN)
      );

      const y = centerY + amplitude * (height * 0.45);
      const x = i * barWidth;

      ctx.lineTo(x, y);
    }

    ctx.closePath();
    ctx.fillStyle = primaryColor;
    ctx.fill();

    if (duration > 0) {
      const progressX = (currentTime / duration) * timelineWidth;
      ctx.fillStyle = primaryColor;
      ctx.globalAlpha = 0.14;
      ctx.fillRect(0, 0, progressX, height);
      ctx.globalAlpha = 1;
    }
  }, [wavePeaks, timelineWidth, duration, currentTime]);

  useEffect(() => {
    if (!dragState || !duration) return;

    const onPointerMove = (event) => {
      if (dragState.pointerId != null && event.pointerId !== dragState.pointerId) {
        return;
      }

      const clientX = getClientXFromEvent(event);
      if (clientX == null) return;

      const deltaSeconds = (clientX - dragState.initialClientX) / effectivePxPerSecond;
      setSegments((prev) =>
        prev.map((segment) => {
          if (segment.id !== dragState.segmentId) return segment;

          const { prevSegment, nextSegment } = getSegmentNeighbors(prev, segment.id);
          const minLeftBound = prevSegment ? prevSegment.end : 0;
          const maxRightBound = nextSegment ? nextSegment.start : duration;

          if (dragState.type === "move") {
            const segmentDuration = dragState.initialEnd - dragState.initialStart;
            const nextStart = clamp(
              dragState.initialStart + deltaSeconds,
              minLeftBound,
              Math.max(minLeftBound, maxRightBound - segmentDuration)
            );
            const nextEnd = nextStart + segmentDuration;
            return { ...segment, start: nextStart, end: nextEnd };
          }

          if (dragState.type === "left") {
            const nextStart = clamp(
              dragState.initialStart + deltaSeconds,
              minLeftBound,
              dragState.initialEnd - MIN_SEGMENT_DURATION
            );
            return { ...segment, start: nextStart };
          }

          const nextEnd = clamp(
            dragState.initialEnd + deltaSeconds,
            dragState.initialStart + MIN_SEGMENT_DURATION,
            maxRightBound
          );
          return { ...segment, end: nextEnd };
        })
      );
    };

    const onPointerUp = (event) => {
      if (dragState.pointerId != null && event.pointerId !== dragState.pointerId) {
        return;
      }
      setDragState(null);
      setActiveSegmentId(null);
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);

    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [dragState, duration, effectivePxPerSecond]);

  const seekTo = (event) => {
    if (suppressNextSeekRef.current) {
      suppressNextSeekRef.current = false;
      return;
    }
    if (dragState) return;
    if (!duration || !timelineRef.current || !videoRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = getClientXFromEvent(event);
    if (clientX == null) return;
    const x = clamp(clientX - rect.left, 0, rect.width);
    const nextTime = (x / rect.width) * duration;
    videoRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const seekWaveform = (event) => {
    if (suppressNextSeekRef.current) {
      suppressNextSeekRef.current = false;
      return;
    }
    if (dragState) return;
    if (!duration || !waveformRef.current || !videoRef.current) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const clientX = getClientXFromEvent(event);
    if (clientX == null) return;

    const x = clamp(clientX - rect.left, 0, rect.width);
    const nextTime = (x / rect.width) * duration;
    videoRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const seekToClientX = useCallback((clientX) => {
    if (!duration || !timelineRef.current || !videoRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width);
    const nextTime = (x / rect.width) * duration;
    videoRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, [duration]);

  const onTimelinePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (dragState) return;

    // Keep native horizontal scrolling smooth on touch devices.
    if (event.pointerType === "touch") {
      return;
    }

    const target = event.target;
    if (target?.closest?.(`.${styles.segment}`) || target?.closest?.(`.${styles.handle}`)) {
      return;
    }

    const clientX = getClientXFromEvent(event);
    if (clientX == null) return;

    setIsScrubbing(true);
    seekToClientX(clientX);
  };

  const onWaveformPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (dragState) return;

    // Keep native horizontal scrolling smooth on touch devices.
    if (event.pointerType === "touch") {
      return;
    }

    const clientX = getClientXFromEvent(event);
    if (clientX == null) return;

    setIsScrubbing(true);
    seekToClientX(clientX);
  };

  const togglePlayPause = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (video.paused) {
        await video.play();
      } else {
        video.pause();
      }
    } catch (e) {
      console.error("Toggle play/pause error", e);
    }
  }, []);

  const startDrag = (event, segment, type) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    suppressNextSeekRef.current = true;
    const clientX = getClientXFromEvent(event);
    if (clientX == null) return;
    setActiveSegmentId(segment.id);
    setDragState({
      segmentId: segment.id,
      type,
      pointerId: typeof event.pointerId === "number" ? event.pointerId : null,
      initialClientX: clientX,
      initialStart: segment.start,
      initialEnd: segment.end,
    });
  };

  const addSegmentAtCursor = () => {
    if (!duration) return;
    const segmentLength = 5;
    let added = false;

    setSegments((prev) => {
      const sorted = [...prev].sort((a, b) => a.start - b.start);
      let start = clamp(currentTime, 0, Math.max(0, duration - segmentLength));
      let end = clamp(start + segmentLength, 0, duration);

      for (const existing of sorted) {
        const overlaps = start < existing.end && end > existing.start;
        if (overlaps) {
          start = existing.end;
          end = start + segmentLength;
        }
      }

      if (end > duration) {
        end = duration;
        start = Math.max(0, end - segmentLength);
      }

      const stillOverlaps = sorted.some((existing) => start < existing.end && end > existing.start);
      if (stillOverlaps || end - start < MIN_SEGMENT_DURATION) {
        return prev;
      }

      added = true;
      return [...prev, { id: `${Date.now()}-${prev.length}`, start, end }].sort((a, b) => a.start - b.start);
    });

    if (!added) {
      setAddSegmentWarning("Non si può aggiungere un altro segmento in questo punto.");
      if (addSegmentWarningTimeoutRef.current) {
        clearTimeout(addSegmentWarningTimeoutRef.current);
      }
      addSegmentWarningTimeoutRef.current = setTimeout(() => {
        setAddSegmentWarning("");
      }, 2400);
      return;
    }

    setAddSegmentWarning("");
  };

  const removeSegment = useCallback((id) => {
    setSegments((prev) => prev.filter((segment) => segment.id !== id));
    setSelectedSegmentIds((prev) => prev.filter((segmentId) => segmentId !== id));
    if (activeSegmentId === id) {
      setActiveSegmentId(null);
    }
  }, [activeSegmentId]);

  const askRemoveSegment = useCallback((id) => {
    setDeleteSegmentDialog({ open: true, segmentId: id });
  }, []);

  const confirmRemoveSegment = useCallback(() => {
    if (deleteSegmentDialog.segmentId) {
      removeSegment(deleteSegmentDialog.segmentId);
    }
    setDeleteSegmentDialog({ open: false, segmentId: null });
  }, [deleteSegmentDialog.segmentId, removeSegment]);

  const openSegmentDialog = (segment) => {
    setSegmentDialog({
      open: true,
      segmentId: segment.id,
      start: formatSecondsToHms(segment.start),
      end: formatSecondsToHms(segment.end),
    });
  };

  const saveSegmentFromDialog = () => {
    const start = parseHmsToSeconds(segmentDialog.start);
    const end = parseHmsToSeconds(segmentDialog.end);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return;
    }

    setSegments((prev) =>
      prev.map((segment) => {
        if (segment.id !== segmentDialog.segmentId) return segment;
        const { prevSegment, nextSegment } = getSegmentNeighbors(prev, segment.id);
        const minLeftBound = prevSegment ? prevSegment.end : 0;
        const maxRightBound = nextSegment ? nextSegment.start : (duration || Math.max(start, end));

        const nextStart = clamp(Math.min(start, end), 0, duration || Math.max(start, end));
        const nextEnd = clamp(Math.max(start, end), 0, duration || Math.max(start, end));

        const boundedStart = clamp(nextStart, minLeftBound, Math.max(minLeftBound, maxRightBound - MIN_SEGMENT_DURATION));
        const boundedEnd = clamp(nextEnd, boundedStart + MIN_SEGMENT_DURATION, maxRightBound);

        if (nextEnd - nextStart < MIN_SEGMENT_DURATION) {
          return segment;
        }
        return { ...segment, start: boundedStart, end: boundedEnd };
      })
    );

    setSegmentDialog({ open: false, segmentId: null, start: "", end: "" });
  };

  const skip = (delta) => {
    if (!videoRef.current) return;
    const nextTime = clamp(videoRef.current.currentTime + delta, 0, duration || Number.MAX_SAFE_INTEGER);
    videoRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const setRate = (rate) => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
  };

  const playbackRateOptions = useMemo(
    () => [
      { value: 1, icon: OneXIcon, label: "1x" },
      { value: 1.5, icon: OneHalfXIcon, label: "1.5x" },
      { value: 2, icon: TwoXIcon, label: "2x" },
    ],
    []
  );

  const activePlaybackIndex = useMemo(() => {
    const index = playbackRateOptions.findIndex((option) => option.value === playbackRate);
    return index >= 0 ? index : 0;
  }, [playbackRate, playbackRateOptions]);

  const sortedSegments = useMemo(() => {
    return [...segments].sort((a, b) => a.start - b.start);
  }, [segments]);

  const ActivePlaybackIcon = playbackRateOptions[activePlaybackIndex].icon;
  const activePlaybackLabel = playbackRateOptions[activePlaybackIndex].label;

  const cyclePlaybackRate = useCallback(() => {
    const nextIndex = (activePlaybackIndex + 1) % playbackRateOptions.length;
    setRate(playbackRateOptions[nextIndex].value);
  }, [activePlaybackIndex, playbackRateOptions]);

  const handleZoomChange = useCallback((event) => {
    centerOnCursorAfterZoomRef.current = true;
    setZoom(Number(event.target.value));
  }, []);

  const saveSegments = async (createJob = false) => {
    try {
      setSaveStatus(ButtonStatus.Loading);
      const payload = {
        segments: segments.map(({ start, end }) => ({
          start: Number(start.toFixed(3)),
          end: Number(end.toFixed(3)),
        })),
      };
      await api.put(`/admin/lessons/${lesson_id}/segments?create_job=${createJob ? "true" : "false"}`, payload);
      setSaveStatus(ButtonStatus.Success);
      setSaveDialogOpen(false);
    } catch (e) {
      console.error(e);
      setSaveStatus(ButtonStatus.Error);
    }
  };

  const majorTickSeconds = useMemo(() => {
    if (!duration) return 10;

    const candidates = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1200, 1800, 3600];
    const minMajorPx = 60;

    for (const step of candidates) {
      if (step * effectivePxPerSecond >= minMajorPx) {
        return step;
      }
    }

    return candidates[candidates.length - 1];
  }, [duration, effectivePxPerSecond]);

  const minorTickSeconds = useMemo(() => {
    if (!duration) return 1;

    const candidates = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1200, 1800];
    const minMinorPx = 12;

    for (const step of candidates) {
      if (step > majorTickSeconds) {
        break;
      }
      if (step * effectivePxPerSecond >= minMinorPx) {
        return step;
      }
    }

    return majorTickSeconds;
  }, [duration, effectivePxPerSecond, majorTickSeconds]);

  const timelineTicks = useMemo(() => {
    if (!duration) return [];
    const ticks = [];
    for (let t = 0; t <= duration; t += majorTickSeconds) {
      ticks.push(t);
    }
    if (ticks[ticks.length - 1] !== duration) {
      ticks.push(duration);
    }
    return ticks;
  }, [duration, majorTickSeconds]);

  useEffect(() => {
    if (!isScrubbing) return;

    const onPointerMove = (event) => {
      const clientX = getClientXFromEvent(event);
      if (clientX == null) return;
      seekToClientX(clientX);
    };

    const onPointerUp = () => {
      setIsScrubbing(false);
    };

    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);

    return () => {
      document.body.style.userSelect = "";
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [isScrubbing, seekToClientX]);

  useEffect(() => {
    if (!rawWavePeaks.length || !timelineWidth) return;

    // Number of samples scales with zoom (timelineWidth grows with zoom)
    const targetSamples = Math.max(300, Math.floor(timelineWidth));
    const peaks = [];

    for (let i = 0; i < targetSamples; i++) {
      const ratio = i / targetSamples;
      const exactIndex = ratio * (rawWavePeaks.length - 1);

      const lower = Math.floor(exactIndex);
      const upper = Math.ceil(exactIndex);
      const weight = exactIndex - lower;

      const lowerValue = rawWavePeaks[lower] || 0;
      const upperValue = rawWavePeaks[upper] || 0;

      // Linear interpolation for smoother zoom scaling
      const value = (1 - weight) * lowerValue + weight * upperValue;

      peaks.push(value);
    }

    setWavePeaks(peaks);
  }, [rawWavePeaks, timelineWidth]);

  const timelineMinorTickPx = useMemo(
    () => Math.max(1, effectivePxPerSecond * minorTickSeconds),
    [effectivePxPerSecond, minorTickSeconds]
  );
  const timelineMajorTickPx = useMemo(
    () => Math.max(1, effectivePxPerSecond * majorTickSeconds),
    [effectivePxPerSecond, majorTickSeconds]
  );
  const activeSegment = useMemo(() => segments.find((segment) => segment.id === activeSegmentId) || null, [segments, activeSegmentId]);

  const handleVideoTimeUpdate = useCallback((event) => {
    const video = event.currentTarget;
    const current = video.currentTime;

    if (!skipSegmentsPlayback || video.paused || sortedSegments.length === 0) {
      setCurrentTime(current);
      return;
    }

    let nextTime = current;
    let guard = 0;

    while (guard < 10) {
      let overlapping = null;
      for (const segment of sortedSegments) {
        if (nextTime >= segment.start && nextTime < segment.end) {
          overlapping = segment;
          break;
        }
      }

      if (!overlapping) {
        break;
      }

      nextTime = Math.min((duration || Number.MAX_SAFE_INTEGER), overlapping.end + 0.02);
      guard += 1;
    }

    if (nextTime !== current) {
      video.currentTime = nextTime;
      setCurrentTime(nextTime);
      return;
    }

    setCurrentTime(current);
  }, [skipSegmentsPlayback, sortedSegments, duration]);

  return (
    <HeaderFooterLayout hideFooter hideHeader>
      <HelmetProvider>
        <Helmet>
          <title>Recordings editor</title>
        </Helmet>
      </HelmetProvider>

      <div className={styles.container}>
        <button
          className={styles.exitButtonFixed}
          onClick={() => window.location.href = '/jobs'}
          title="Esci"
        >
          <ExitIcon className={styles.exitIcon} />
        </button>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.content} onMouseDownCapture={handleOutsideSegmentClick}>
              <div className={styles.topPane}>
                <div className={styles.videoPane}>
                  <video
                    playsInline={true}
                    ref={videoRef}
                    className={styles.video}
                    src={videoUrl}
                    controls
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    onTimeUpdate={handleVideoTimeUpdate}
                    onLoadedMetadata={(event) => {
                      const d = Number(event.currentTarget.duration || 0);
                      setDuration(d);
                      setDisplayCurrentTime(Number(event.currentTarget.currentTime || 0));
                      if (segments.length > 0) {
                        setSegments((prev) => normalizeSegments(prev, d));
                      }
                    }}
                  />
                </div>
                <div className={styles.sidePanel}>
                  <div className={styles.sideHeaderRow}>
                    <div className={styles.sideHeader}>{videoTitle || "Video"}</div>
                    <div className={styles.headerActions}>
                      <button className={styles.addSegmentButton} onClick={addSegmentAtCursor} title="Aggiungi segmento">
                        <AddIcon className={styles.addSegmentIcon} />
                      </button>
                      <button
                        className={styles.saveSegmentButton}
                        onClick={() => setSaveDialogOpen(true)}
                        disabled={!lesson_id || saveStatus === ButtonStatus.Loading}
                        title="Salva segmenti"
                      >
                        <CheckIcon className={styles.saveSegmentIcon} />
                      </button>
                    </div>
                  </div>
                  <div className={styles.segmentList}>
                    {!!addSegmentWarning && <div className={styles.addSegmentWarning}>{addSegmentWarning}</div>}
                    {segments.map((segment, index) => (
                      <div
                        key={segment.id}
                        className={`${styles.segmentRow} ${selectedSegmentIds.includes(segment.id) ? styles.segmentRowSelected : ""}`}
                      >
                        <button
                          className={styles.segmentRowMain}
                          onClick={(event) => {
                            if (event.metaKey) {
                              event.preventDefault();
                              toggleSegmentSelection(segment.id);
                              return;
                            }
                            openSegmentDialog(segment);
                          }}
                        >
                          <div>#{index + 1}</div>
                          <div>{formatSecondsDuration(segment.start)} - {formatSecondsDuration(segment.end)}</div>
                        </button>
                        <button
                          className={styles.removeSegmentButton}
                          onClick={() => askRemoveSegment(segment.id)}
                          title="Rimuovi"
                        >
                          <CloseIcon className={styles.removeSegmentIcon} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.bottomPane}>
                <div className={styles.controls}>
                  <div className={styles.leftControls}>
                    <div className={styles.mobileControlsGroup}>
                      <button className={styles.iconControl} onClick={togglePlayPause} title={isPlaying ? "Pausa" : "Play"}>
                        {isPlaying ? <PauseIcon className={styles.controlIcon} /> : <PlayIcon className={styles.controlIcon} />}
                      </button>
                      <button className={styles.iconControl} onClick={() => skip(-10)} title="Indietro 10 secondi">
                        <TenBackwardIcon className={styles.controlIcon} />
                      </button>
                    </div>
                    <div className={styles.controlsCenterTimeMobile}>
                      {formatSecondsDuration(displayCurrentTime)} / {formatSecondsDuration(duration)}
                    </div>
                    <div className={styles.mobileControlsGroup}>
                      <button className={styles.iconControl} onClick={() => skip(10)} title="Avanti 10 secondi">
                        <TenForwardIcon className={styles.controlIcon} />
                      </button>
                      <button
                        className={`${styles.iconControl} ${skipSegmentsPlayback ? styles.activeRate : ""}`}
                        onClick={(event) => {
                          setSkipSegmentsPlayback((prev) => !prev);
                          event.currentTarget.blur();
                        }}
                        title="Riproduci saltando i segmenti"
                      >
                        <SkipTabIcon className={styles.controlIcon} />
                      </button>
                    </div>
                  </div>

                  <div className={styles.controlsCenterTimeDesktop}>
                    {formatSecondsDuration(displayCurrentTime)} / {formatSecondsDuration(duration)}
                  </div>

                  <div className={styles.rightControls}>
                    <button
                      className={styles.rateToggle}
                      onClick={cyclePlaybackRate}
                      title={`Velocità ${activePlaybackLabel}`}
                    >
                      <ActivePlaybackIcon className={styles.controlIcon} />
                    </button>

                    <div className={styles.zoomWrap}>
                      <input
                        className={styles.zoomSlider}
                        id="timeline-zoom"
                        aria-label="Zoom"
                        style={{ "--zoom-progress": `${((zoom - 1) / Math.max(zoomMax - 1, 1)) * 100}%` }}
                        type="range"
                        min="1"
                        max={zoomMax}
                        step="0.1"
                        value={zoom}
                        onChange={handleZoomChange}
                      />
                      <span className={styles.zoomValue}>{zoom.toFixed(1)}x</span>
                    </div>
                  </div>
                </div>

                <div ref={timelineViewportRef} className={styles.timelineViewport}>
                  <div className={styles.timelineTrack} style={{ width: timelineWidth }}>
                    <div className={styles.ticksLayer}>
                      {timelineTicks.map((tick) => (
                        <div key={tick} className={styles.tick} style={{ left: `${(tick / duration) * 100}%` }}>
                          <span>{formatTimelineTickLabel(tick)}</span>
                        </div>
                      ))}
                    </div>

                    <div
                      ref={timelineRef}
                      className={`${styles.timeline} ${dragState?.type === "move" ? styles.grabbing : ""}`}
                      onClick={seekTo}
                      onPointerDown={onTimelinePointerDown}
                      style={{
                        "--minor-tick-step": `${timelineMinorTickPx}px`,
                        "--major-tick-step": `${timelineMajorTickPx}px`,
                      }}
                    >
                      <div className={styles.timelineTickMarks} />
                      <div className={styles.playhead} style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                      {segments.map((segment) => (
                        <div
                          key={segment.id}
                          className={`${styles.segment} ${selectedSegmentIds.includes(segment.id) ? styles.segmentSelected : ""} ${dragState?.type === "move" && dragState?.segmentId === segment.id ? styles.segmentDragging : ""}`}
                          style={{
                            left: `${duration ? (segment.start / duration) * 100 : 0}%`,
                            width: `${duration ? ((segment.end - segment.start) / duration) * 100 : 0}%`,
                          }}
                          onPointerDown={(event) => {
                            if (event.metaKey) {
                              event.preventDefault();
                              event.stopPropagation();
                              suppressNextSeekRef.current = true;
                              toggleSegmentSelection(segment.id);
                              return;
                            }
                            startDrag(event, segment, "move");
                          }}
                        >
                          <button
                            className={`${styles.handle} ${styles.leftHandle}`}
                            onPointerDown={(event) => startDrag(event, segment, "left")}
                          />
                          <div className={styles.segmentLabel}>{formatSecondsDuration(segment.start)} - {formatSecondsDuration(segment.end)}</div>
                          <button
                            className={`${styles.handle} ${styles.rightHandle}`}
                            onPointerDown={(event) => startDrag(event, segment, "right")}
                          />
                        </div>
                      ))}
                    </div>

                    <div ref={waveformRef} className={styles.waveform} style={{ width: timelineWidth }} onPointerDown={onWaveformPointerDown} onClick={seekWaveform}>
                      {!loadingWaveform && wavePeaks.length === 0 && (
                        <div className={styles.wavePlaceholder}>Forma d'onda non disponibile (controlla CORS/url)</div>
                      )}
                      {loadingWaveform && <div className={styles.wavePlaceholder}>Generazione forma d'onda...</div>}
                      <canvas ref={waveformCanvasRef} className={styles.waveCanvas} />
                      {activeSegment && duration > 0 && (
                        <>
                          <div
                            className={`${styles.segmentGuide} ${styles.segmentGuideStart}`}
                            style={{ left: `${(activeSegment.start / duration) * 100}%` }}
                          />
                          <div
                            className={`${styles.segmentGuide} ${styles.segmentGuideEnd}`}
                            style={{ left: `${(activeSegment.end / duration) * 100}%` }}
                          />
                        </>
                      )}
                      <div className={styles.playhead} style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <AlertDialog
              open={saveDialogOpen}
              title="Salvataggio segmenti"
              onClose={() => setSaveDialogOpen(false)}
              status={DialogStatus.Default}
              text="Vuoi salvare solo i segmenti oppure salvare e avviare anche il job di processamento video?"
              actions={[
                {
                  label: "SALVA SEGMENTI",
                  status: saveStatus === ButtonStatus.Loading ? ButtonStatus.Loading : null,
                  onClick: async () => saveSegments(false),
                },
                {
                  label: "SALVA E PROCESSA",
                  status: saveStatus === ButtonStatus.Loading ? ButtonStatus.Loading : null,
                  onClick: async () => saveSegments(true),
                },
              ]}
            />

            <Dialog
              open={segmentDialog.open}
              title="Modifica segmento"
              onClose={() => setSegmentDialog({ open: false, segmentId: null, start: "", end: "" })}
              style={{ width: "560px", maxWidth: "92vw" }}
              action={(
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <Button appearance="text" onClick={() => setSegmentDialog({ open: false, segmentId: null, start: "", end: "" })}>Annulla</Button>
                  <Button onClick={saveSegmentFromDialog}>Salva</Button>
                </div>
              )}
            >
              <div style={{ display: "grid", gap: "0.9rem" }}>
                <label style={{ display: "grid", gap: "0.35rem" }}>
                  <span>Inizio (HH:MM:SS)</span>
                  <input
                    className={styles.textarea}
                    style={{ minHeight: "42px" }}
                    value={segmentDialog.start}
                    onChange={(event) => setSegmentDialog((prev) => ({ ...prev, start: event.target.value }))}
                    placeholder="00:00:00"
                  />
                </label>
                <label style={{ display: "grid", gap: "0.35rem" }}>
                  <span>Fine (HH:MM:SS)</span>
                  <input
                    className={styles.textarea}
                    style={{ minHeight: "42px" }}
                    value={segmentDialog.end}
                    onChange={(event) => setSegmentDialog((prev) => ({ ...prev, end: event.target.value }))}
                    placeholder="00:00:00"
                  />
                </label>
              </div>
            </Dialog>

            <AlertDialog
              open={deleteSegmentDialog.open}
              title="Eliminare il segmento?"
              text="Sei sicuro di voler eliminare questo segmento?"
              onClose={() => setDeleteSegmentDialog({ open: false, segmentId: null })}
              actions={[
                {
                  label: "ANNULLA",
                  inverse: true,
                  onClick: async () => setDeleteSegmentDialog({ open: false, segmentId: null }),
                },
                {
                  label: "ELIMINA",
                  accentColor: "var(--sf-red)",
                  onClick: confirmRemoveSegment,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  );
};

export default RecordingsEditor;